import React, { useState } from 'react';
import DataService from "./../services/data.service";
import { AuthenticatedUserModel } from "./../types/AuthenticatedUserModel";
import { DataResult, Result, TokenResult } from "./../types/Result";

interface AuthContextValue {
    user: AuthenticatedUserModel | null,
    isAuthenticated: () => boolean,
    isAuthorizedFor: (applicationFunction: ApplicationFunctions) => boolean,
    isAuthorizedForAttachmentType: (applicationFunction: ApplicationFunctions, attachmentTypeId?: number) => boolean,
    isAuthorizedForItemType: (applicationFunction: ApplicationFunctions, itemTypeId?: number) => boolean,
    isAuthorizedForPriceType: (applicationFunction: ApplicationFunctions, priceTypeId?: number) => boolean,
    isAuthorizedForProjectType: (applicationFunction: ApplicationFunctions, projectTypeId?: number) => boolean,
    isAuthorizedForWorkflowDefinition: (applicationFunction: ApplicationFunctions, workflowDefinitionId?: number) => boolean,
    isAuthorizedForImportType: (applicationFunction: ApplicationFunctions, importTypeId?: number) => boolean,
    isAuthorizedForExportType: (applicationFunction: ApplicationFunctions, exportTypeId?: number) => boolean,
    login: (username: string, password: string, connectionStringName: string, callback: (result: DataResult<AuthenticatedUserModel>) => void) => void;
    logout: () => void;
    logActivity: () => void;
    changeLanguage: (language: string) => void;
    changeProfilePicture: (url: string) => void;
    changeNextTimeLogin: () => void;

}

const AuthContext = React.createContext<AuthContextValue | null>(null);

const AuthProvider: React.FC = (props) => {
    const [user, setUser] = useState<AuthenticatedUserModel | null>(getUser());

    function getUser() {
        const sUser = localStorage.getItem("user");
        if (sUser !== null)
            return JSON.parse(sUser);

        return null;
    }

    const login = function (username: string, password: string, connectionStringName: string, callback: (result: DataResult<AuthenticatedUserModel>) => void): void {
        let environment = ''
        var hostname = window.location.hostname;
        switch (hostname) {
            case 'localhost':
                environment = 'dev'
                break;
            case 'plmtst2':
            case 'plmtest.difuzed.com':
                environment = 'tst'
                break;
            case 'plm2':
            case 'plm.difuzed.com':
                environment = 'prd'
                break;
        }

        DataService.login(username, password, connectionStringName, environment)
            .then(result => {
                if (result.isSuccesful) {
                    if (result.data) {
                        let authenticatedUser = result.data;
                        localStorage.setItem("user", JSON.stringify(authenticatedUser));

                        setUser({ ...authenticatedUser });
                    }
                }

                callback(result);
            })
            .catch((e) => {
                callback({ isSuccesful: false, data: undefined, messages: [{ message: { format: e.message, args: [] }, messageType: 5, memberNames: [] }] })
            });
    }

    const logout = function() {
        localStorage.removeItem("user");
        setUser(null);
    }

    const logActivity = function () {
    }

    const changeLanguage = function (language: string): void {
        console.log('Change languange', language)
        if (user)
            DataService
                .post<TokenResult>("api/v1/users/changelanguage/" + language, {})
                .then(result => {
                    if (result.isSuccesful) {
                        user.language = language
                        user.token = result.token
                        user.tokenExpiresOn = result.expires
                        localStorage.setItem("user", JSON.stringify(user));
                        setUser({ ...user });
                    }
                })
    }

    const changeProfilePicture = function (url: string): void {
        if (user) {
            user.thumbnailUrl = url;
            localStorage.setItem("user", JSON.stringify(user));
            setUser({ ...user });
        }
    }

    const changeNextTimeLogin = function (): void {
        if (user) {
            user.changePasswordOnNextLogin = false;
            localStorage.setItem("user", JSON.stringify(user));
            setUser({ ...user });
        }
    }

    function isAuthenticated(): boolean {
        if (!user)
            return false;

        if (new Date(user.tokenExpiresOn) < new Date(Date.now()))
            return false;

        return true;
    }

    function isAuthorizedFor(applicationFunction: ApplicationFunctions) {
        if (!user)
            return false;
        
        if (user.isAdmin)
            return true;
        
        //console.log(user);

        for (let i = 0; i < user.applicationFunctionIds.length; i++) {
            if (user.applicationFunctionIds[i] == applicationFunction) {
                //console.log('Authorized 1');
                return true;
            }
        }

        return false;
    }

    function isAuthorizedForAttachmentType(applicationFunction: ApplicationFunctions, attachmentTypeId?: number) {
        if (!user)
            return false;

        if (isAuthorizedFor(applicationFunction))
            return true

        if (user.applicationFunctionIdsForAttachmentType) {
            if (attachmentTypeId) {
                if (user.applicationFunctionIdsForAttachmentType[attachmentTypeId] === undefined) {
                    return false;
                }

                for (let i = 0; i < user.applicationFunctionIdsForAttachmentType[attachmentTypeId].length; i++) {
                    if (user.applicationFunctionIdsForAttachmentType[attachmentTypeId][i] == applicationFunction) {
                        //console.log('Authorized attachmentType');
                        return true;
                    }
                }
            }
            else {
                for (let key in user.applicationFunctionIdsForAttachmentType) {
                    for (let i = 0; i < user.applicationFunctionIdsForAttachmentType[key].length; i++) {
                        if (user.applicationFunctionIdsForAttachmentType[key][i] == applicationFunction) {
                            console.log('Authorized any attachmentType');
                            return true;
                        }
                    }
                }
            }
        }

        return false;
    }

    function isAuthorizedForItemType(applicationFunction: ApplicationFunctions, itemTypeId?: number) {
        if (!user)
            return false;

        if (isAuthorizedFor(applicationFunction))
            return true

        if (user.applicationFunctionIdsForItemType) {
            if (itemTypeId) {
                if (user.applicationFunctionIdsForItemType[itemTypeId] === undefined) {
                    return false;
                }

                for (let i = 0; i < user.applicationFunctionIdsForItemType[itemTypeId].length; i++) {
                    if (user.applicationFunctionIdsForItemType[itemTypeId][i] == applicationFunction) {
                        //console.log('Authorized itemType for function: ' + applicationFunction);
                        //console.log(user.applicationFunctionIdsForItemType[itemTypeId][i]);
                        return true;
                    }
                }
            }
            else {
                for (let key in user.applicationFunctionIdsForItemType) {
                    for (let i = 0; i < user.applicationFunctionIdsForItemType[key].length; i++) {
                        if (user.applicationFunctionIdsForItemType[key][i] == applicationFunction) {
                            //console.log('Authorized any itemType for function:' + applicationFunction);
                            return true;
                        }
                    }
                }
            }
        }

        return false;
    }

    function isAuthorizedForPriceType(applicationFunction: ApplicationFunctions, priceTypeId?: number) {
        if (!user)
            return false;

        if (isAuthorizedFor(applicationFunction))
            return true

        if (user.applicationFunctionIdsForPriceType) {
            if (priceTypeId) {
                if (user.applicationFunctionIdsForPriceType[priceTypeId] === undefined) {
                    return false;
                }

                for (let i = 0; i < user.applicationFunctionIdsForPriceType[priceTypeId].length; i++) {
                    if (user.applicationFunctionIdsForPriceType[priceTypeId][i] == applicationFunction) {
                        //console.log('Authorized priceType');
                        return true;
                    }
                }
            }
            else {
                for (let key in user.applicationFunctionIdsForPriceType) {
                    for (let i = 0; i < user.applicationFunctionIdsForPriceType[key].length; i++) {
                        if (user.applicationFunctionIdsForPriceType[key][i] == applicationFunction) {
                            //console.log('Authorized any priceType for function:' + applicationFunction);
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }

    function isAuthorizedForProjectType(applicationFunction: ApplicationFunctions, projectTypeId?: number) {
        if (!user)
            return false;

        if (isAuthorizedFor(applicationFunction))
            return true

        if (user.applicationFunctionIdsForProjectType) {
            if (projectTypeId) {
                if (user.applicationFunctionIdsForProjectType[projectTypeId] === undefined) {
                    return false;
                }

                for (let i = 0; i < user.applicationFunctionIdsForProjectType[projectTypeId].length; i++) {
                    if (user.applicationFunctionIdsForProjectType[projectTypeId][i] == applicationFunction) {
                        //console.log('Authorized projectType');
                        return true;
                    }
                }
            }
            else {
                for (let key in user.applicationFunctionIdsForProjectType) {
                    for (let i = 0; i < user.applicationFunctionIdsForProjectType[key].length; i++) {
                        if (user.applicationFunctionIdsForProjectType[key][i] == applicationFunction) {
                            //console.log('Authorized any projectType for function:' + applicationFunction);
                            return true;
                        }
                    }
                }
            }
        }

        return false;
    }

    function isAuthorizedForWorkflowDefinition(applicationFunction: ApplicationFunctions, workflowDefinitionId?: number) {
        if (!user)
            return false;

        if (isAuthorizedFor(applicationFunction))
            return true

        if (user.applicationFunctionIdsForWorkflowDefinition) {
            if (workflowDefinitionId) {
                if (user.applicationFunctionIdsForWorkflowDefinition[workflowDefinitionId] === undefined) {
                    return false;
                }

                for (let i = 0; i < user.applicationFunctionIdsForWorkflowDefinition[workflowDefinitionId].length; i++) {
                    if (user.applicationFunctionIdsForWorkflowDefinition[workflowDefinitionId][i] == applicationFunction) {
                        //console.log('Authorized workflowDefinition');
                        return true;
                    }
                }
            }
            else {
                for (let key in user.applicationFunctionIdsForWorkflowDefinition) {
                    for (let i = 0; i < user.applicationFunctionIdsForWorkflowDefinition[key].length; i++) {
                        if (user.applicationFunctionIdsForWorkflowDefinition[key][i] == applicationFunction) {
                            //console.log('Authorized any workflowDefinition for function:' + applicationFunction);
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }

    function isAuthorizedForImportType(applicationFunction: ApplicationFunctions, importTypeId?: number) {
        if (!user)
            return false;

        if (isAuthorizedFor(applicationFunction))
            return true

        if (user.importTypeIds) {
            if (importTypeId) {
                for (let i = 0; i < user.importTypeIds.length; i++) {
                    if (user.importTypeIds[i] == importTypeId) {
                        return true;
                    }
                }
            }
            else {
                return true;
            }
        }
        return false;
    }

    function isAuthorizedForExportType(applicationFunction: ApplicationFunctions, exportTypeId?: number) {
        if (!user)
            return false;

        if (isAuthorizedFor(applicationFunction))
            return true

        if (user.exportTypeIds) {
            if (exportTypeId) {
                for (let i = 0; i < user.exportTypeIds.length; i++) {
                    if (user.exportTypeIds[i] == exportTypeId) {
                        return true;
                    }
                }
            }
            else {
                return true;
            }
        }
        return false;
    }

    return <AuthContext.Provider value={{ user, changeLanguage, changeProfilePicture, changeNextTimeLogin, isAuthenticated, isAuthorizedFor, isAuthorizedForAttachmentType, isAuthorizedForItemType, isAuthorizedForPriceType, isAuthorizedForProjectType, isAuthorizedForWorkflowDefinition, isAuthorizedForImportType, isAuthorizedForExportType, login, logout, logActivity }} {...props} />;
};

function useAuth(): AuthContextValue {
    const context = React.useContext(AuthContext);
    if (context === null) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
export { AuthProvider, useAuth };

export enum ApplicationFunctions {
    ViewHomeScreen = 1,
    ViewLatestStatusChanges = 2,
    ViewMenuItem = 3,
    EditMenuItem = 4,

    StartImportProcess = 5,
    StartServiceJob = 6,

    ViewTenant = 7,
    ChangeTenantLogo = 8,

    CreateUser = 9,
    ViewUser = 10,
    UpdateUser = 11,
    DeleteUser = 12,
    ViewMyProfile = 13,
    ChangeProfilePicture = 14,
    SetOutOfOffice = 15,
    EditApplicationRoleForUser = 16,
    EnableDisableUser = 17,
    UnlockUser = 18,
    ResetPassword = 19,

    CreateApplicationRole = 20,
    ViewApplicationRole = 21,
    UpdateApplicationRole = 22,
    DeleteApplicationRole = 23,

    CreateFunctionalRole = 24,
    ViewFunctionalRole = 25,
    UpdateFunctionalRole = 26,
    DeleteFunctionalRole = 27,

    CreateDepartment = 28,
    ViewDepartment = 29,
    UpdateDepartment = 30,
    DeleteDepartment = 31,
    AddEmployeeToDepartment = 32,

    CreateEmployee = 33,
    ViewEmployee = 34,
    UpdateEmployee = 35,
    DeleteEmployee = 36,
    EditApplicationRoleForEmployee = 37,
    EditFunctionalRoleForEmployee = 38,

    ViewGender = 39,
    EditGender = 40,

    CreateBusinessPartner = 41,
    ViewBusinessPartner = 42,
    UpdateBusinessPartner = 43,
    DeleteBusinessPartner = 44,

    CreateAddress = 45,
    ViewAddress = 46,
    UpdateAddress = 47,
    DeleteAddress = 48,

    CreateContactperson = 49,
    ViewContactperson = 50,
    UpdateContactperson = 51,
    DeleteContactperson = 52,

    ViewMessage = 57,
    EditMessage = 58,

    ViewCalendar = 59,
    EditCalendar = 60,
    ViewNotification = 61,
    ViewForumDiscussion = 62,
    EditForumDiscussion = 63,
    ParticipateInForumDiscussion = 64,
    ModerateForumDiscussionComments = 65,

    ViewNews = 66,
    EditNews = 67,

    CreateItem = 68,
    ViewAllItems = 69,
    UpdateItem = 70,
    DeleteItem = 71,
    ExportItem = 72,
    ViewMyItems = 73,
    ViewItemsOfType = 74,
    CreateItemOfType = 77,
    CreateItemRevision = 78,
    ViewItemRevision = 79,
    UpdateItemRevision = 80,
    DeleteItemRevision = 81,
    ChangeItemNr = 82,
    CheckOutItemRevision = 83,
    EditItemPrice = 88,
    EditItemComment = 89,
    CloneItem = 90,
    FollowItem = 91,

    CreateBillOfMaterial = 92,
    ViewBillOfMaterial = 93,
    UpdateBillOfMaterial = 94,
    DeleteBillOfMaterial = 95,

    CreateLicense = 96,
    ViewLicense = 97,
    UpdateLicense = 98,
    DeleteLicense = 99,

    SearchItemRevision = 100,
    SearchItemRevisionAdvanced = 101,
    SearchProject = 102,
    SearchLicense = 103,
    SearchWorkflow = 104,
    SearchWorkflowTask = 105,
    AddSelectedItemsToProject = 106,

    CreateProject = 107,
    ViewAllProjects = 108,
    ViewMyProjects = 109,
    UpdateProject = 110,
    DeleteProject = 111,
    ViewProjectChangeLog = 112,

    ViewProjectType = 113,
    EditProjectType = 114,
    ViewProjectStatus = 115,
    EditProjectStatus = 116,

    StartItemWorkflow = 117,
    StartProjectWorkflow = 118,
    ViewWorkflow = 119,
    ViewAllWorkflowTasks = 120,
    ViewMyWorkflowTasks = 121,
    ExecuteAllWorkflowTasks = 122,
    StopWorkflow = 123,
    CancelWorkflow = 124,
    SuspendWorkflow = 125,
    DeleteWorkflow = 126,
    AssignWorkflowTaskToMe = 127,
    ReAssignWorkflowTask = 128,
    UndoWorkflowTask = 129,

    CreateWorkflowDefinition = 130,
    ViewWorkflowDefinition = 131,
    UpdateWorkflowDefinition = 132,
    DeleteWorkflowDefinition = 133,

    ViewPanels = 134,
    EditPanels = 135,

    CreateAttachment = 136,
    ViewAttachment = 137,
    DeleteAttachment = 138,
    DownloadAttachment = 139,

    GenerateTechpack = 140,
    GenerateApprovalSheet = 141,
    GenerateSalesPresentation = 142,

    ViewItemType = 143,
    EditItemType = 144,
    ViewBomTemplate = 145,
    EditBomTemplate = 146,
    ViewItemStatusType = 147,
    EditItemStatusType = 148,
    ViewItemStatus = 149,
    EditItemStatus = 150,
    ViewProductCategory = 151,
    EditProductCategory = 152,
    ViewBrand = 153,
    EditBrand = 154,
    ViewUdfCategory = 155,
    EditUdfCategory = 156,
    ViewUdf = 157,
    EditUdf = 158,
    ViewAddressType = 159,
    EditAddressType = 160,
    ViewRegion = 161,
    EditRegion = 162,
    ViewCountry = 163,
    EditCountry = 164,
    ViewCurrency = 165,
    EditCurrency = 166,
    ViewPriceType = 167,
    EditPriceType = 168,
    ViewPriceTrancheType = 169,
    EditPriceTrancheType = 170,

    ViewItemPrice = 171,
    ViewItemComment = 172,
    ViewItemFollower = 173,
    ViewItemHistory = 174,
    ViewItemChangeLog = 175,
    ParticipateInItemDiscussion = 176,

    ViewItemSetup = 177,
    ViewAppConfig = 178,
    EditAppConfig = 179,

    ViewDesignPhase = 180,
    EditDesignPhase = 181,

    CreateBusinessPartnerGroup = 182,
    ViewBusinessPartnerGroup = 183,
    UpdateBusinessPartnerGroup = 184,
    DeleteBusinessPartnerGroup = 185,

    ViewIncoTerms = 186,
    EditIncoTerms = 187,

    ViewRoyaltyTypes = 188,
    EditRoyaltyTypes = 189,

    ViewAttachmentTypes = 190,
    EditAttachmentTypes = 191,
    ViewDocumentTemplates = 192,
    EditDocumentTemplates = 193,

    ViewTranslations = 194,
    VerifyTranslation = 195,
    ApproveTranslation = 196,
    SynchronizeTranslations = 197,

    ViewMilestoneDefintions = 198,
    EditMilestoneDefintions = 199,

    SearchAttachments = 200,

    ViewExternalSystem = 201,
    EditExternalSystem = 202,

    ViewProjectItemDetailInfo = 203,
    EditProjectItemDetailInfo = 204,
    ViewProjectDiscussion = 205,
    ParticipateInProjectDiscussion = 206,

    EmployeeMaintenance = 207,
    ExecuteMyWorkflowTasks = 208,

    ViewSize = 209,
    EditSize = 210,
    ViewColor = 211,
    EditColor = 212,

    ViewDivisions = 213,
    EditDivisions = 214,
}